import Immutable from 'seamless-immutable';
import _ from 'lodash';

import { assetRefService } from '../../common/Services';

/**
 * User
 * @typedef {Object} User
 * @property {string} id - user id
 * @property {string} name - user name
 * @property {string} email - user email
 * @property {object} experienceAdminActions - key value pairs outlining the administrator actions the current user can perform
 * @property {object} timeZone - user time zone
 * @property {object} permissions - user permissions for various actions
 * @property {string} phone - a string that can have () and + along with numbers that is meant to describe the users phone number
 * @property {bool} cessso - a boolean value to determine if the currently logged in user, users sessions, is managed by cheetah external services, for email type users this one should be false and false by default
 * @property {string} jobTitle - a handwritten piece of text describing the users role, currently only used to display info
 * @property {string} cheetahIdProfileUrl - an external url that tends to be used when `cessso===true` that we use to redirect the user to on request
 * @property {string} cheetahIdBaseUrl - an external url that tends to be used when `cessso===true` that we use mostly for NavKit to have a base CESSSO url
 * @property {string} cesLastInvited - date, when the user has been invited to the Cheetah ID (is empty if not using Cheetah ID)
 */

/**
 * Create a User from provided data
 */
function create(data = {}) {
  const result = _.clone(data);
  return Immutable(result, { prototype: model });
}

/**
 *
 * [Victor] I don't think this one is currently used at all ... i do wonder if it would be a good idea to have this one called and then parse them via keys for properties we might make use of ?
 *
 *
 * Transforms a user object into a UserModel
 * @param {Object} user - formatted user object
 * @returns {User}
 */
function deserialize(user) {
  const result = _.pick(user, _keys);
  return create(result);
}

/**
 * Transform a UserModel into a user object
 * @returns {Object}
 */
function serialize(userModel) {
  const result = userModel.asMutable({ deep: true });
  return _.pick(result, _keys);
}

const model = {
  get id() {
    return _.get(window, 'App.globalContext.user.id', '');
  },
  get avatar() {
    return _.get(window, 'App.globalContext.user.avatar', '');
  },
  get avatarRef() {
    return assetRefService.buildAssetRef(
      'static',
      'image',
      '35x35',
      `/c/img/icons/avatar/${this.avatar.replace('.', '-')}.svg`
    );
  },
  get name() {
    return _.get(window, 'App.globalContext.user.name', '');
  },
  get email() {
    return _.get(window, 'App.globalContext.user.email', '');
  },
  get experienceAdminActions() {
    return {
      ...(_.get(window, 'App.globalContext.user.role.features.allowDeleteExperience', false) && {
        canDeleteExperience: true,
      }),
    };
  },
  get timeZone() {
    return _.get(window, 'App.globalContext.userTimeZone', { name: 'Europe/London', offset: '0' });
  },

  get mainRoleName() {
    return App.globalContext.user.role.features.mainRoleName;
  },

  get mainRole() {
    return App.globalContext.user.role.features.mainRole;
  },

  get locale() {
    return App.globalContext.user.locale;
  },

  get firstName() {
    return _.get(window, 'App.globalContext.user.firstName', '');
  },

  get lastName() {
    return _.get(window, 'App.globalContext.user.lastName', '');
  },

  get twoFactorAuthEnabled() {
    return _.get(window, 'App.globalContext.user.twoFactorAuthEnabled', '');
  },

  get additionalRolesAssigned() {
    return _.get(window, 'App.globalContext.user.additionalRolesAssigned', []);
  },

  get permissions() {
    const _perms = {
      canExportContent:         _.get(window, 'App.globalContext.user.role.features.canExportContent', false),
      canAdministerCollections: _.get(window, 'App.globalContext.user.role.features.creator', false),
      canAdministerContent:     _.get(window, 'App.globalContext.user.role.features.canAdministerContent', false),
      canAdministerFeeds:       _.get(window, 'App.globalContext.user.role.features.creator', false),

      canAdministerLibrary:           _.get(window, 'App.globalContext.user.role.features.canAdministerLibrary', false),
      canAdministerWinner:            _.get(window, 'App.globalContext.user.role.features.canAdministerWinner', false),
      canViewEntries:                 _.get(window, 'App.globalContext.user.role.features.canViewEntries', false),
      canViewIntegrationDebugDetails: _.get(
        window,
        'App.globalContext.user.role.features.canViewIntegrationDebugDetails',
        false
      ),
      canAccessIntegrationCredentials: _.get(
        window,
        'App.globalContext.user.role.features.canAccessIntegrationCredentials',
        false
      ),
      canDeleteExperience: _.get(window, 'App.globalContext.user.role.features.allowDeleteExperience'),
      canSwitchAccount:    _.get(window, 'App.globalContext.account.master', false),
      canViewExperiences:  _.get(window, 'App.globalContext.user.role.features.canViewExperiences', false),
      canManageForms:      _.get(window, 'App.globalContext.user.role.features.canManageForms', false),
      isInsights:          _.get(window, 'App.globalContext.user.role.features.insights', false),
      isCreator:           _.get(window, 'App.globalContext.user.role.features.creator', false),
      isPublisher:         _.get(window, 'App.globalContext.user.role.features.publisher', false),
      isManager:           _.get(window, 'App.globalContext.user.role.features.manager', false),
      isAdmin:             _.get(window, 'App.globalContext.user.role.features.admin', false),
      isSupport:           _.get(window, 'App.globalContext.user.role.features.support', false),

      //**************************//
      //Integration Specific Access
      //The integrations areas require a more granular gating to feature access
      //**************************//
      canAccessIntegrationList:    _.get(window, 'App.globalContext.user.role.features.canAccessIntegrationList', false),
      canEnableDisableIntegration: _.get(
        window,
        'App.globalContext.user.role.features.canEnableDisableIntegration',
        false
      ),
      canChangeIntegrationName: _.get(window, 'App.globalContext.user.role.features.canChangeIntegrationName', false),
      canUpdateIntegration:     _.get(window, 'App.globalContext.user.role.features.canUpdateIntegration', false),
      canCreateIntegration:     _.get(window, 'App.globalContext.user.role.features.canCreateIntegration', false),
      canDeleteIntegration:     _.get(window, 'App.globalContext.user.role.features.canDeleteIntegration', false),

      canAccessRewardsList:    _.get(window, 'App.globalContext.user.role.features.canAccessRewardsList', false),
      canEditCesssoUsers:      _.get(window, 'App.globalContext.user.role.features.canEditCesssoUsers', false),
      canAccessCollectionList: _.get(window, 'App.globalContext.user.role.features.canAccessCollectionList', false),
    };

    _perms.canExportWinners = _perms.canExportContent || _perms.canAdministerWinner;

    return _perms;
  },

  /**
   * checks if the user can see the "Content" item in the global navigation
   */
  get canViewContentSection() {
    //https://docs.google.com/spreadsheets/d/1cFrUzUdEa4knVxUeo2rDuvdfR6KWfS0AJQxJ8Hp9szo/edit#gid=1659842607
    const permissions = this.permissions;
    return (
      permissions.canAdministerContent ||
      permissions.canExportContent ||
      permissions.isCreator ||
      permissions.isPublisher ||
      permissions.isManager ||
      permissions.isAdmin ||
      permissions.isSupport
    );
  },

  get phone() {
    return _.get(window, 'App.globalContext.user.phone', '');
  },

  get cessso() {
    return _.get(window, 'App.globalContext.user.cessso', '');
  },

  get cesLastInvited() {
    return _.get(window, 'App.globalContext.user.cesLastInvited', '');
  },

  get cesAuthLoginURL() {
    return _.get(window, 'App.globalContext.user.cesAuthLoginURL', '');
  },

  get cesAuthApiURL() {
    return _.get(window, 'App.globalContext.user.cesAuthApiURL', '');
  },

  get cesSsoSessionExpiredLink() {
    return _.get(window, 'App.globalContext.user.cesExpiredURL', '');
  },

  get jobTitle() {
    return _.get(window, 'App.globalContext.user.jobTitle', '');
  },

  get cheetahIdProfileUrl() {
    return _.get(window, 'App.globalContext.user.cheetahIdProfileUrl', '');
  },

  get cheetahIdBaseUrl() {
    return _.get(window, 'App.globalContext.user.cheetahIdBaseUrl', '');
  },

  get fullName() {
    return _.get(window, 'App.globalContext.user.name', '');
  },
};

const _keys = [];

export default {
  create,
  deserialize,
  serialize,
};
